import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import foodPublicApi from "../api/foodPublicApi";

const FoodSearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const initialPage = parseInt(searchParams.get("page") || "1", 10);

  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [page, setPage] = useState(Number.isNaN(initialPage) ? 1 : initialPage);
  const [foods, setFoods] = useState([]);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthenticated(Boolean(token));
  }, []);
  const handleBackHome = () => navigate("/");
  const handleSignIn = () => {
    if (isAuthenticated) {
      navigate("/app/dashboard");
    } else {
      navigate("/login");
    }
  };
  const handleSignUp = () => navigate("/signup");

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedQuery(query.trim());
    }, 400);

    return () => clearTimeout(handler);
  }, [query]);

  useEffect(() => {
    const params = {};
    if (debouncedQuery) params.q = debouncedQuery;
    if (page > 1) params.page = page.toString();
    setSearchParams(params, { replace: true });
  }, [debouncedQuery, page, setSearchParams]);

  useEffect(() => {
    const fetchFoods = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await foodPublicApi.search({
          query: debouncedQuery,
          page,
          limit: 25,
        });
        setFoods(response?.data || []);
        setPagination(
          response?.pagination || { totalItems: 0, totalPages: 0 }
        );
      } catch (err) {
        const message =
          err?.data?.message ||
          err?.statusText ||
          "Unable to load foods right now.";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchFoods();
  }, [debouncedQuery, page]);

  const showingRange = useMemo(() => {
    if (!pagination.totalItems) return "";
    const from = (page - 1) * 25 + 1;
    const to = from + foods.length - 1;
    return `${from} – ${to} of ${pagination.totalItems.toLocaleString()} items`;
  }, [foods.length, page, pagination.totalItems]);

  const handlePrev = () => setPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () =>
    setPage((prev) =>
      pagination.totalPages ? Math.min(prev + 1, pagination.totalPages) : prev + 1
    );

  const baseUrl = "https://www.caloriespal.com";
  const canonicalUrl = `${baseUrl}/foods${debouncedQuery ? `?q=${encodeURIComponent(debouncedQuery)}` : ""}`;
  const pageTitle = debouncedQuery
    ? `Search “${debouncedQuery}” | CaloriesPal Food Explorer`
    : "CaloriesPal Food Explorer | Discover Ingredients & Nutrients";
  const pageDescription =
    "Search thousands of professionally curated foods, review accurate nutrient facts, and explore public ingredient data powered by CaloriesPal.";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "CaloriesPal Food Explorer",
    url: canonicalUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: `${baseUrl}/foods?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <div className="bg-slate-950 min-h-screen text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <meta
          name="keywords"
          content="food nutrition data, ingredient database, nutrition explorer, caloriespal foods, food nutrients"
        />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="CaloriesPal" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image:alt" content="CaloriesPal Food Explorer" />
        <meta property="og:image" content={`${baseUrl}/assets/images/Calories (1).png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${baseUrl}/assets/images/Calories (1).png`} />
        <meta name="twitter:site" content="@CaloriesPal" />
        <meta name="twitter:creator" content="@CaloriesPal" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-950 to-black py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(132,204,22,0.25),_transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-8">
            <button
              onClick={handleBackHome}
              className="inline-flex items-center gap-2 text-sm font-semibold text-lime-200 hover:text-white transition"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white/20 text-xs">
                ←
              </span>
              Back to homepage
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSignIn}
                className="rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-white hover:border-lime-400 hover:text-lime-100 transition"
              >
                {isAuthenticated ? "Dashboard" : "Sign in"}
              </button>
              {!isAuthenticated && (
                <button
                  onClick={handleSignUp}
                  className="rounded-full bg-white text-slate-900 px-5 py-2 text-sm font-semibold shadow-lg hover:bg-lime-100 transition"
                >
                  Sign up
                </button>
              )}
            </div>
          </div>
          <p className="text-sm uppercase tracking-[0.4em] text-lime-300 mb-4">
            Food Intelligence
          </p>
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight text-white drop-shadow">
            Explore Ingredients & Nutrients
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-3xl">
            Search thousands of FDA-backed foods, inspect their nutrient
            profiles, and uncover actionable macro breakdowns. Everything is open
            to the public—no login required.
          </p>
          <form className="mt-10 space-y-4" onSubmit={(e) => e.preventDefault()}>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl">
                🔎
              </span>
              <input
                type="text"
                className="w-full rounded-full border border-white/10 bg-white/5 px-14 pr-40 py-4 text-lg text-white placeholder-slate-300 shadow-[0_25px_70px_rgba(0,0,0,0.35)] focus:outline-none focus:ring-2 focus:ring-lime-400"
                placeholder="Search by ingredient, category, nutrient or brand"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-lime-400 to-emerald-400 px-5 py-2 text-sm font-semibold text-slate-900 shadow-lg hover:opacity-90 transition"
                onClick={() => setDebouncedQuery(query.trim())}
              >
                Search
              </button>
            </div>
            <div className="flex flex-wrap gap-2 text-sm text-slate-400">
              <span className="px-3 py-1 rounded-full border border-white/10">
                Example: chickpea flour
              </span>
              <span className="px-3 py-1 rounded-full border border-white/10">
                Example: sodium mg
              </span>
              <span className="px-3 py-1 rounded-full border border-white/10">
                Example: beverage
              </span>
            </div>
          </form>
          <p className="mt-4 text-sm text-slate-400">
            Tip: Click on any ingredient row to open a detail dashboard.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Search Results</h2>
            <p className="text-sm text-slate-400">{showingRange}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrev}
              disabled={page === 1 || loading}
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-lime-400 hover:text-lime-300 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs uppercase tracking-[0.4em] text-slate-400">
              Page {page}
              {pagination.totalPages ? ` / ${pagination.totalPages}` : ""}
            </span>
            <button
              onClick={handleNext}
              disabled={
                loading || (pagination.totalPages ? page >= pagination.totalPages : false)
              }
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-lime-400 hover:text-lime-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>

        <div className="bg-white/5 rounded-3xl border border-white/10 overflow-hidden backdrop-blur">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-12 h-12 border-4 border-lime-300 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-12 text-center text-rose-300 font-semibold">
              {error}
            </div>
          ) : foods.length === 0 ? (
            <div className="p-12 text-center text-slate-300">
              Start typing to discover ingredients from the CaloriesPal database.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {foods.map((food) => (
                <button
                  key={food._id || food.fdcId}
                  onClick={() => navigate(`/foods/${food.slug || food.fdcId}`)}
                  className="w-full text-left px-6 py-5 hover:bg-white/5 transition flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                >
                  <div>
                    <p className="text-lg font-semibold text-white">
                      {food.description || "Unnamed Ingredient"}
                    </p>
                    <p className="text-sm text-slate-400">
                      {food.foodCategory?.description || "General"} ·{" "}
                      {food.foodClass || "Unknown class"}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-slate-400 bg-black/30 px-3 py-1 rounded-full">
                      FDC ID: {food.fdcId}
                    </span>
                    <span className="inline-flex items-center gap-2 text-lime-300 text-sm">
                      View details
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 3l6 5-6 5" />
                      </svg>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default FoodSearch;
